import axiosInstance from './api'
import {
	fixtureFetchUniversityById,
	fixtureFetchUniversityCourses,
	shouldUseApplicationFlowFixtures,
} from 'fixtures/applicationFlowFixtures'
import {
	isGatewayTimeoutError,
	recoverShortlistAfterTimeout,
	toShortlistSnapshot,
} from 'shared/utils/recoverShortlistAfterTimeout'

export const fetchUniversityById = async universityId => {
	const id = String(universityId || '').trim()
	if (!id) throw new Error('Invalid universityId')
	if (shouldUseApplicationFlowFixtures()) return fixtureFetchUniversityById(id)

	const response = await axiosInstance.get(`/shortlister/universities/${id}`)
	return response.data
}

const courseMatches = (course, courseId) =>
	[course?.id, course?.course_id].some(value => String(value || '').trim() === courseId)

/**
 * Courses at a university. Pass courseId to get only that course (matched on the
 * description id or the generic course id) instead of every course at the university.
 */
export const fetchUniversityCourses = async (universityId, courseId = '') => {
	const id = String(universityId || '').trim()
	if (!id) throw new Error('Invalid universityId')
	const onlyCourse = String(courseId || '').trim()
	if (shouldUseApplicationFlowFixtures()) {
		const all = fixtureFetchUniversityCourses(id)
		return onlyCourse ? all.filter(course => courseMatches(course, onlyCourse)) : all
	}

	const response = await axiosInstance.get(
		`/shortlister/university/${encodeURIComponent(id)}/course`,
		onlyCourse ? { params: { course_id: onlyCourse } } : undefined,
	)
	return response.data
}

/**
 * Course live-status for Requirements / Fee / Essays / Deadlines.
 * Same contract as student shortlisterApi.getCourseLiveStatus.
 * `ucdId` must be univ_course_desc.id.
 */
export const getCourseLiveStatus = async ucdId => {
	const id = String(ucdId || '').trim()
	if (!id) throw new Error('Invalid course description id')
	const response = await axiosInstance.get(
		`/shortlister/course-desc/${encodeURIComponent(id)}/live-status`,
	)
	return response.data
}

/**
 * Enqueue Perplexity refresh for one section.
 * Same contract as student shortlisterApi.postCourseLiveRefresh.
 */
export const postCourseLiveRefresh = async (ucdId, section) => {
	const id = String(ucdId || '').trim()
	if (!id) throw new Error('Invalid course description id')
	const response = await axiosInstance.post(
		`/shortlister/course-desc/${encodeURIComponent(id)}/live-refresh`,
		{ section },
	)
	return response.data
}

/**
 * Load all categories with nested subcategories in one request.
 * Same contract as student AI Tools getCourseGroupsAndSubGroups.
 * @param {string|null} degree Bachelors | Masters | null
 */
export async function getCourseGroupsAndSubGroups(degree) {
	return axiosInstance.post('shortlister/courses/group/subgroups', {
		degree: degree ?? null,
	})
}

/**
 * Same contract as student AI Tools getProfessionsCourse.
 * Returns raw axios response — consumer uses response.data[0].
 */
export async function getProfessionsCourse(selected, degree) {
	return await axiosInstance.post(
		`shortlister/courses/group/subgroup/professions`,
		{
			selected: selected,
			degree: degree,
		},
	)
}

/**
 * Same contract as student AI Tools getProfessionsCountry.
 * Returns raw axios response — consumer uses response.data[0].
 * @param {string} selected career title
 */
export async function getProfessionsCountry(selected) {
	return await axiosInstance.post(`shortlister/professions/countries`, {
		selected: selected,
	})
}

/**
 * Same contract as student AI Tools getSpecialization (Course Finder).
 * Returns raw axios response — consumer uses response.data[0].
 */
export async function getSpecialization(category, subCategory, country, degree) {
	return await axiosInstance.post(
		`shortlister/courses/group/subgroup/specialization`,
		{
			category: category,
			subCategory: subCategory,
			country: country,
			degree: degree,
		},
	)
}

export async function getAllShortlisters(params) {
	return await axiosInstance.get('shortlister', { params })
}

export async function getShortlisterById(id, page = 1) {
	return await axiosInstance.get(`shortlister/${id}`, {
		params: { page },
	})
}

export async function getSortShortlisterById(
	id,
	page = 1,
	sort_by = 'deadline',
	sort_order = 'desc',
) {
	return await axiosInstance.get(`shortlister/${id}`, {
		params: { page, sort_by, sort_order },
	})
}

// The API picks the shortlist owner from `student_id`: from the query for GET /shortlister, and from the JSON body
// for POST /shortlister (_shortlist_owner_id in flaskr/controllers/shortlister.py). The snapshot and poll must
// read the same owner as the POST, so a body student_id is also sent as the GET query param.
const shortlistOwnerParams = data => (data?.student_id ? { student_id: data.student_id } : undefined)

const fetchShortlistRows = async params => (await getAllShortlisters(params)).data

// Creates or regenerates a shortlist. If the gateway times out, the save is still running on the server, so we
// wait for it to appear (see recoverShortlistAfterTimeout) and never re-send the POST. The snapshot of existing
// shortlists starts alongside the POST, so the success path gets no extra latency.
export async function createShortlist(data) {
	const startedAt = Date.now()
	const params = shortlistOwnerParams(data)
	const snapshotPromise = fetchShortlistRows(params)
		.then(toShortlistSnapshot)
		.catch(error => {
			console.warn('Shortlist snapshot failed; it will be taken at failure time', error)
			return null
		})
	try {
		return await axiosInstance.post('shortlister', data)
	} catch (error) {
		if (!isGatewayTimeoutError(error, Date.now() - startedAt)) throw error
		const saved = await recoverShortlistAfterTimeout({
			startedAt,
			baseline: await snapshotPromise,
			fetchShortlists: () => fetchShortlistRows(params),
		})
		if (!saved) throw error
		return { status: 200, data: saved }
	}
}

export async function getCities(countryObj) {
	return await axiosInstance.post('shortlister/cities', countryObj)
}

export async function getAutoFillShortlisterCourses() {
	return await axiosInstance.get('/shortlister/courses_grouped')
}

export async function getAllCoursesFromShortlistUni(shortlist_id, university_id) {
	return await axiosInstance.get(
		`shortlister/${shortlist_id}/university/${university_id}/courses`,
	)
}
